import { useState, useEffect } from 'react';
import { Navigate, useLocation, Outlet, createHashRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AuthLayout from '@Root/AuthLayout';
import BaseOutlet from '@Root/BaseOutlet';
import ErrorFallBack from '@Root/ErrorFallBack';
import LoginPage from '@LandingPages/LoginPage';
import SignupPage from '@LandingPages/SignupPage';
import OnboardingPage from '@LandingPages/OnboardingPage';
import OrgSelectionPage from '@LandingPages/OrgSelectionPage';
import ForgotPasswordPage from '@LandingPages/ForgotPasswordPage';
import LandingPage from '@LandingPages/LandingPage';
import SubscribePage from '@BillingPages/SubscribePage';
import SubscriptionExpiredPage from '@BillingPages/SubscriptionExpiredPage';
import CheckoutPage from '@BillingPages/CheckoutPage';
import CheckoutSuccessPage from '@BillingPages/CheckoutSuccessPage';
import SplashScreen from '@LandingPages/SplashScreen';
import PricingPage from '@LandingPages/PricingPage';
import WhyKaizIQPage from '@LandingPages/WhyKaizIQPage';
import BlogsPage from '@LandingPages/BlogsPage';
import ChangelogPage from '@LandingPages/ChangelogPage';
import CustomerSupportPage from '@LandingPages/CustomerSupportPage';
import CaseStudiesPage from '@LandingPages/CaseStudiesPage';
import EbooksPage from '@LandingPages/EbooksPage';
import EnterprisePage from '@LandingPages/EnterprisePage';
import SMBPage from '@LandingPages/SMBPage';
import StartupsPage from '@LandingPages/StartupsPage';
import NonprofitsPage from '@LandingPages/NonprofitsPage';
import ITHelpDeskPage from '@LandingPages/ITHelpDeskPage';
import HROperationsPage from '@LandingPages/HROperationsPage';
import FacilitiesPage from '@LandingPages/FacilitiesPage';
import CustomerComplaintsPage from '@LandingPages/CustomerComplaintsPage';
import InternalFeedbackPage from '@LandingPages/InternalFeedbackPage';
import EducationPage from '@LandingPages/EducationPage';
import HealthcarePage from '@LandingPages/HealthcarePage';
import RetailPage from '@LandingPages/RetailPage';
import FinancialServicesPage from '@LandingPages/FinancialServicesPage';
import ManufacturingPage from '@LandingPages/ManufacturingPage';
import GetStartedPage from '@LandingPages/GetStartedPage';
import LogoutPage from '@LandingPages/LogoutPage';
import ProfilePage from '@UserManagementPages/ProfilePage';
import TicketTrackPage from '@IssuePages/TicketTrackPage';
import DocsPage from '@ProjectPages/DocsPage';
import RegistriesPage from '@RegistriesPages/RegistriesPage';
import FormSearchPage from '@FormPages/FormSearchPage';
import FormPage from "@FormPages/FormPage";
import SettingsPage from '@UserManagementPages/SettingsPage';
import TeamsUsersPage from '@UserManagementPages/TeamsUsersPage';
import { default as IssueOverviewPage } from '@IssueConsolePages/OverviewPage';
import DashboardPage from '@IssueConsolePages/DashboardPage/DashboardPage';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxToken } from '@Hooks/reduxTokenHook/useReduxToken';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import BillingService from '@Helpers/URLHelpers/BillingService';

const NoIndex = () => (
    <Helmet>
        <meta name="robots" content="noindex, nofollow" />
    </Helmet>
);

const renderLandingPages = (param, setToken, setEntryPage) => {

    switch (param) {
        case 'login':
            return <LoginPage setToken={setToken} setEntryPage={setEntryPage} />;
        case 'signup':
            return <SignupPage setToken={setToken} setEntryPage={setEntryPage} />;
        case 'forgotPassword':
            return <ForgotPasswordPage setEntryPage={setEntryPage} />;
        default:
            return <LoginPage setToken={setToken} setEntryPage={setEntryPage} />;
    }

};

const LoginPageWrapper = () => {

    const location = useLocation();
    const { token, setToken } = useReduxToken();
    const { userType } = useReduxActiveUser();
    const [entryPage, setEntryPage] = useState('login');
    const defaultDest = userType === 'admin' ? { pathname: '/dashboard', search: '' } : { pathname: '/home', search: '' };
    const from = location.state?.from || defaultDest;

    if (!!token) {
        return <Navigate to={from} replace />;
    }
    return renderLandingPages(entryPage, setToken, setEntryPage);
};

const RouteMismatchWrapper = () => {

    const location = useLocation();

    return <Navigate to='/home' state={{ from: location }} replace />;
}

const OnboardingWrapper = () => {

    const { token } = useReduxToken();
    const location = useLocation();
    const { currentProject } = useReduxProject();
    const { currentOrg } = useReduxOrg();

    if (!token) {
        return <Navigate to='/login' state={{ from: location }} replace />;
    }
    if (!!currentProject && currentOrg?.visibility === 'resolve') {
        return <Navigate to='/dashboard' state={{ from: location }} replace />;
    }
    return <OnboardingPage />;
}

const AdminGuard = ({ children }) => {

    const { token } = useReduxToken();
    const location = useLocation();
    const { userType, activeUserAccessList } = useReduxActiveUser();
    const { currentProject } = useReduxProject();

    if (!token) {
        return <Navigate to="/login" replace />;
    }
    if (!currentProject) {
        const resolveOrgs = (activeUserAccessList?.orgs || []).filter(o => o.visibility === 'resolve');
        if (resolveOrgs.length > 1) {
            return <Navigate to='/org-select' state={{ from: location }} replace />;
        }
        return <Navigate to='/onboarding' state={{ from: location }} replace />;
    }
    if (userType !== "admin") {
        const params = new URLSearchParams(location.search);
        const ticketId = params.get('ticketId');
        if (ticketId) {
            const dest = new URLSearchParams();
            dest.set('ticketId', ticketId);
            const formId = params.get('formId');
            if (formId) dest.set('formId', formId);
            return <Navigate to={`/home/track?${dest.toString()}`} replace />;
        }
        return <Navigate to="/home" replace />;
    }

    return <><NoIndex />{children}</>;
};

const ReporterGuard = ({ children }) => {

    const { token } = useReduxToken();
    const { userType } = useReduxActiveUser();
    const location = useLocation();

    const isTrackPage = location.pathname === '/home/track';
    const isUserManagementPage = location.pathname.startsWith('/userManagement');

    if (isTrackPage && !token) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (userType === "admin") {
        if (isUserManagementPage) {
            return <><NoIndex />{children}</>;
        }
        if (isTrackPage) {
            const params = new URLSearchParams(location.search);
            const formId = params.get('formId');
            const ticketId = params.get('ticketId');
            return <Navigate
                to="/forms"
                state={{ deepLink: { formId, ticketId } }}
                replace
            />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return <><NoIndex />{children}</>;
};

const ProjectGuard = () => {

    const { currentProject } = useReduxProject();

    if (!currentProject) {
        return <Navigate to="/onboarding" replace />;
    }

    return <Outlet />;
};

const LandingPageWrapper = () => {

    const location = useLocation();
    const { currentProject } = useReduxProject();
    const { userType } = useReduxActiveUser();

    if (!!currentProject) {
        if (userType === 'admin') {
            return <Navigate to='/dashboard' state={{ from: location }} replace />;
        }
        return <Navigate to='/home' state={{ from: location }} replace />;
    }
    return <LandingPage />;
}

const billingService = new BillingService();

const SubscriptionGuard = ({ children }) => {

    const location = useLocation();
    const { currentOrg, currentOrgId } = useReduxOrg();
    const { activeUserId } = useReduxActiveUser();
    const { subscription, isSubscriptionValid, setCurrentSubscription } = useReduxSubscription();
    const [checking, setChecking] = useState(false);
    const [checked, setChecked] = useState(false);
    const hasMatchingOrgSubscription =
        subscription !== null &&
        String(subscription?.orgId || '') === String(currentOrgId || '');

    useEffect(() => {
        if (!currentOrgId) {
            // Org not loaded yet — stay on SplashScreen until it resolves
            return;
        }
        if (hasMatchingOrgSubscription) {
            setChecked(true);
            return;
        }
        // subscription is missing or belongs to another org — fetch fresh for current org
        setChecked(false);
        setChecking(true);
        billingService.getSubscriptionByOrgId(currentOrgId)
            .then((data) => {
                if (data?.body) {
                    try {
                        const parsed = JSON.parse(data.body);
                        const sub = parsed?.data ?? parsed;
                        setCurrentSubscription(sub);
                    } catch (_) {
                        setCurrentSubscription(null);
                    }
                } else {
                    setCurrentSubscription(null);
                }
            })
            .catch(() => {
                setCurrentSubscription(null);
            })
            .finally(() => {
                setChecking(false);
                setChecked(true);
            });
    }, [currentOrgId, hasMatchingOrgSubscription, setCurrentSubscription]);

    if (checking || !checked) {
        return <SplashScreen />;
    }

    if (!isSubscriptionValid) {
        
        const isOwner = !currentOrg || currentOrg.ownerUserId === activeUserId;
        return isOwner
            ? <Navigate to='/subscribe' state={{ from: location }} replace />
            : <Navigate to='/subscription-expired' state={{ from: location }} replace />;
    }

    return children;
};

const OrgSelectWrapper = () => {

    const { token } = useReduxToken();
    const { currentProject } = useReduxProject();
    const location = useLocation();

    if (!token) {
        return <Navigate to='/login' state={{ from: location }} replace />;
    }
    if (currentProject) {
        return <Navigate to='/dashboard' state={{ from: location }} replace />;
    }
    return <OrgSelectionPage />;
};

const Routes = createHashRouter([
    {
        path: '/',
        element: <LandingPageWrapper />,
    },
    {
        path: 'pricing',
        element: <PricingPage />,
    },
    {
        path: 'why-kaiziq',
        element: <WhyKaizIQPage />,
    },
    {
        path: 'blogs',
        element: <BlogsPage />,
    },
    {
        path: 'changelog',
        element: <ChangelogPage />,
    },
    {
        path: 'support',
        element: <CustomerSupportPage />,
    },
    {
        path: 'case-studies',
        element: <CaseStudiesPage />,
    },
    {
        path: 'ebooks',
        element: <EbooksPage />,
    },
    {
        path: 'solutions',
        children: [
            { path: 'enterprise',           element: <EnterprisePage /> },
            { path: 'smb',                  element: <SMBPage /> },
            { path: 'startups',             element: <StartupsPage /> },
            { path: 'nonprofits',           element: <NonprofitsPage /> },
            { path: 'it-help-desk',         element: <ITHelpDeskPage /> },
            { path: 'hr-operations',        element: <HROperationsPage /> },
            { path: 'facilities',           element: <FacilitiesPage /> },
            { path: 'customer-complaints',  element: <CustomerComplaintsPage /> },
            { path: 'internal-feedback',    element: <InternalFeedbackPage /> },
            { path: 'education',            element: <EducationPage /> },
            { path: 'healthcare',           element: <HealthcarePage /> },
            { path: 'retail',               element: <RetailPage /> },
            { path: 'financial-services',   element: <FinancialServicesPage /> },
            { path: 'manufacturing',        element: <ManufacturingPage /> },
        ],
    },
    {
        path: 'login',
        element: <><NoIndex /><LoginPageWrapper /></>,
    },
    {
        path: 'logout',
        element: <><NoIndex /><LogoutPage /></>,
    },
    {
        path: 'onboarding',
        element: <><NoIndex /><OnboardingWrapper /></>,
    },
    {
        path: 'org-select',
        element: <><NoIndex /><OrgSelectWrapper /></>,
    },
    {
        path: 'subscription-expired',
        element: <><NoIndex /><SubscriptionExpiredPage /></>,
    },
    {
        path: 'home',
        element: (
            <ReporterGuard>
                <BaseOutlet />
            </ReporterGuard>
        ),
        errorElement: <ErrorFallBack />,
        children: [
            {
                index: true,
                element: <GetStartedPage />,
            },
            {
                path: 'track',
                element: <TicketTrackPage />
            },
            {
                path: 'form',
                children: [
                    {
                        path: 'search',
                        element: <FormSearchPage />
                    }
                ]
            },
        ],
    },
    {
        path: 'report/:formId',
        element: <><NoIndex /><FormPage /></>,
    },
    {
        path: 'userManagement',
        element: (
            <ReporterGuard>
                <BaseOutlet />
            </ReporterGuard>
        ),
        errorElement: <ErrorFallBack />,
        children: [
            {
                index: true,
                element: <ProfilePage />,
            },
            {
                path: 'settings',
                element: <SettingsPage />,
            },
        ],
    },
    {
        path: 'subscribe',
        element: <><NoIndex /><SubscribePage /></>,
    },
    {
        path: 'checkout',
        children: [
            { index: true, element: <><NoIndex /><CheckoutPage /></> },
            { path: 'success', element: <><NoIndex /><CheckoutSuccessPage /></> },
        ],
    },
    {
        // Admin-only pages that don't require an active subscription (e.g. settings, billing)
        element: (
            <AdminGuard>
                <AuthLayout />
            </AdminGuard>
        ),
        errorElement: <ErrorFallBack />,
        children: [
            {
                path: 'userManagement/settings',
                element: <SettingsPage />,
            },
        ],
    },
    {
        element: (
            <AdminGuard>
                <SubscriptionGuard>
                    <AuthLayout />
                </SubscriptionGuard>
            </AdminGuard>
        ),
        errorElement: <ErrorFallBack />,
        children: [
            {
                element: <ProjectGuard />,
                children: [
                    {
                        path: 'dashboard',
                        element: <DashboardPage/>
                    },
                    {
                        path: 'forms',
                        element: <IssueOverviewPage />
                    },
                    {
                        path: 'registries',
                        element:  <RegistriesPage/>,
                    },
                    {
                        path: 'docs',
                        element:  <DocsPage/>,
                    },
                    {
                        path: 'userManagement/teams-users',
                        element: <TeamsUsersPage />,
                    },
                ]
            },
        ]
    },
    {
        path: '*',
        element: <RouteMismatchWrapper />,
    },
]);

export default Routes;